import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { formatPhoneNumber } from '../lib/phoneFormat';
import { getRecaptchaToken, isRecaptchaEnabled } from '../lib/recaptcha';

const CoachingPage = () => {
  const location = useLocation();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Handle hash navigation to contact-form
  useEffect(() => {
    if (location.hash === '#contact-form') {
      setTimeout(() => {
        const element = document.getElementById('contact-form');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Please tell us what you\'re looking for';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken('contact_form');
      const response = await fetch(`${API_URL}/api/auth/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          phone: formData.phone.replace(/\D/g, ''),
          interest: 'coaching',
          website: honeypot,
          recaptcha_token: recaptchaToken
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        setErrors({ submit: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setErrors({ submit: 'Unable to submit. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="service-page start-here-page">
      {/* Hero Section */}
      <div className="service-hero start-here-hero">
        <div className="service-hero-content">
          <h1 className="start-here-headline">
            You know something<br />
            <span className="headline-emphasis">needs to move.</span>
          </h1>
          <p className="hero-subtitle">Let's figure out what.</p>
        </div>
      </div>

      <div className="service-content">
        {/* Opening Section */}
        <section className="start-here-section">
          <div className="start-here-content">
            <p className="opening-line">
              This isn't a gym. It's not a wellness program. It's a place for people who are capable, competent, and completely maxed out — and kids who need to get off the ground and off a screen.
            </p>
            
            <p className="body-copy emphasis">
              Here for yourself, your kid, or just getting started?
            </p>
          </div>
        </section>

        {/* Three Doors Section */}
        <section className="three-doors-section">
          <div className="doors-grid">
            {/* Door 01 */}
            <Link to="/bungee" className="door-card">
              <span className="door-number">01</span>
              <h3 className="door-title">Move Your Body First</h3>
              <p className="door-description">
                Bungee fitness classes. Low impact. High fun. You'll feel something shift in the first class — guaranteed.
              </p>
              <p className="door-details">
                Drop-in $35 · 4-pack $130 · Thu–Sun mornings · Wilmington, NC
              </p>
              <span className="door-cta">See the schedule →</span>
            </Link>

            {/* Door 02 */}
            <Link to="/aerial" className="door-card featured">
              <span className="door-badge">Weekly classes enrolling</span>
              <span className="door-number">02</span>
              <h3 className="door-title">Aerial Kids</h3>
              <p className="door-description">
                Weekly aerial classes — slings, Lyra (hoop), silks, aerial pole & bungees on a progressive curriculum. Plus our new Air Academy: aerial + tunnel flying at iFLY.
              </p>
              <p className="door-details">
                Ages 8–12 · Wed & Thu · $125/mo · Wilmington, NC
              </p>
              <span className="door-cta">Register your kid →</span>
            </Link>

            {/* Door 03 */}
            <Link to="/workouts" className="door-card">
              <span className="door-number">03</span>
              <h3 className="door-title">Start Where You Are</h3>
              <p className="door-description">
                Build a workout in 7 seconds. No gym, no gear, no excuse.
              </p>
              <p className="door-details">
                Free workout builder · Free weekly motivation · Start today
              </p>
              <span className="door-cta">Build my workout →</span>
            </Link>
          </div>
        </section>

        {/* Closing Statement */}
        <section className="closing-statement">
          <p>
            Glad you found us. See you soon.
          </p>
        </section>

        {/* Contact Form Section */}
        <section className="scheduling-section" id="contact-form">
          <h2>Have a question?</h2>
          <p className="contact-intro">Not sure where to start?</p>
          <p className="contact-subtext">Tell us what you're looking for — we'll point you in the right direction.</p>
          
          <div className="scheduling-widget-container contact-form-active">
            {isSubmitted ? (
              <div className="contact-success" data-testid="contact-success">
                <CheckCircle size={64} className="success-icon" />
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. I'll get back to you within 24-48 hours.</p>
                <p className="success-note">Check your email for a confirmation.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form" data-testid="coaching-contact-form">
                {/* Honeypot — hidden from real users, bots auto-fill it */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="first_name">First Name *</label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="First name"
                      className={errors.first_name ? 'error' : ''}
                      data-testid="contact-first-name"
                    />
                    {errors.first_name && <span className="error-text">{errors.first_name}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="last_name">Last Name *</label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Last name"
                      className={errors.last_name ? 'error' : ''}
                      data-testid="contact-last-name"
                    />
                    {errors.last_name && <span className="error-text">{errors.last_name}</span>}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className={errors.email ? 'error' : ''}
                      data-testid="contact-email"
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone (optional)</label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(000) 000-0000"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>
                
                <div className="form-group full-width">
                  <label htmlFor="message">What brings you here? *</label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Curious about bungee? Looking at kids' classes or Air Academy? Just browsing? We'd love to hear from you."
                    rows={5}
                    className={errors.message ? 'error' : ''}
                    data-testid="contact-message"
                  />
                  {errors.message && <span className="error-text">{errors.message}</span>}
                </div>
                
                {errors.submit && (
                  <div className="submit-error" data-testid="contact-submit-error">
                    {errors.submit}
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  size="lg" 
                  className="contact-submit-btn"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={18} />
                      Start the conversation
                    </>
                  )}
                </Button>
                {isRecaptchaEnabled() && (
                  <p className="recaptcha-disclosure">
                    Protected by reCAPTCHA — Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a> & <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms</a> apply.
                  </p>
                )}
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CoachingPage;
